import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Button } from '@cloudflare/kumo/components/button';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Empty } from '@cloudflare/kumo/components/empty';
import { Table } from '@cloudflare/kumo/components/table';
import {
  DialogRoot,
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@cloudflare/kumo/components/dialog';
import { ArrowCounterClockwise, CheckSquare, ArrowSquareOut } from '@phosphor-icons/react';
import { api } from '@/lib/api';

// 申込内容の確認 (紙レビュー) が終わった出展者を、まとめて「内容確認済」にする画面。
// status='entered' (当年度) を自動リストし、保留・要再確認の人だけチェックを外して
// 残りを一括 checkedByAdmin にする。メールはここでは送らない (内定通知は /emails)。
// レビューを複数日に分けても、内定メールの到達タイミングを揃えられるよう段階を分けている。

interface PendingRow {
  id: string;
  applicationNumber: number;
  email: string;
  realName: string;
  exhibitorName: string;
  exhibitTitle: string;
  type: string;
  status: string;
  exhibitDays: string[];
}

const TYPE_LABEL: Record<string, string> = {
  general: '一般',
  'student-university': '学生 (大学)',
  'student-high-school': '学生 (高校以下)',
  corporate: '企業',
};

export default function ContentReviewPage() {
  const [pending, setPending] = useState<PendingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewing, setPreviewing] = useState<{
    eligibleCount: number;
    skippedCount: number;
  } | null>(null);
  const [applying, setApplying] = useState(false);

  const selectedCount = selectedIds.size;
  const allChecked = pending.length > 0 && selectedCount === pending.length;
  const anyChecked = selectedCount > 0;

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: PendingRow[] }>(
        '/api/admin/exhibitors/pending-content-review'
      );
      const rows = res.data.data ?? [];
      setPending(rows);
      // 初期表示は全選択 (admin が保留にしたい人だけチェックを外す UX)
      setSelectedIds(new Set(rows.map((r) => r.id)));
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const toggleAll = () => {
    if (allChecked) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pending.map((r) => r.id)));
    }
  };
  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openPreview = async () => {
    if (selectedCount === 0) return;
    setError(null);
    try {
      const res = await api.post('/api/admin/exhibitors/bulk-mark-checked', {
        exhibitorIds: Array.from(selectedIds),
        dryRun: true,
      });
      const data = (res.data?.data ?? res.data) as {
        eligibleCount: number;
        skippedCount: number;
      };
      setPreviewing({
        eligibleCount: data.eligibleCount,
        skippedCount: data.skippedCount,
      });
    } catch (e) {
      setError(extractError(e));
    }
  };

  const confirmApply = async () => {
    if (!previewing) return;
    setApplying(true);
    setError(null);
    try {
      const res = await api.post('/api/admin/exhibitors/bulk-mark-checked', {
        exhibitorIds: Array.from(selectedIds),
        dryRun: false,
      });
      const data = (res.data?.data ?? res.data) as {
        updated: number;
        skipped?: Array<{ exhibitorId: string; reason: string }>;
      };
      setInfo(
        `${data.updated} 件を内容確認済にしました。内定通知メールは「メール送信」画面から一斉送信してください。${
          data.skipped?.length ? ` / スキップ ${data.skipped.length} 件` : ''
        }`
      );
      setPreviewing(null);
      await refresh();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-kumo-default">内容確認</h1>
          <p className="text-sm text-kumo-muted">
            申込内容を確認し、出展 OK を出せる出展者をまとめて「内容確認済」にします。
            保留・要再確認の方はチェックを外してください。ここではメールは送りません。
          </p>
        </div>
        <Button
          variant="secondary"
          icon={ArrowCounterClockwise}
          onClick={refresh}
          loading={loading}
        >
          再読み込み
        </Button>
      </div>

      {error && <Banner variant="error">{error}</Banner>}
      {info && <Banner variant="default">{info}</Banner>}

      <Banner variant="default">
        ここで「内容確認済」にしても<strong>メールは届きません</strong>。
        内定通知メールは内容確認をすべて終えてから「メール送信」画面でまとめて送ります
        （レビューを複数日に分けても、メールの到達タイミングを揃えるためです）。
        個別の内容を読みたいときは出展者番号のリンクから詳細を開けます。
      </Banner>

      <Surface>
        <div className="flex items-center justify-between border-b border-kumo-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-kumo-default">
              内容確認 未処理 ({pending.length})
            </h2>
            <p className="text-xs text-kumo-muted">
              申込完了で、まだ内容確認していない出展者を一覧しています。初期表示は全選択。
              保留・要再確認の方だけチェックを外してください。
            </p>
          </div>
          <Button
            icon={CheckSquare}
            disabled={!anyChecked || loading}
            onClick={openPreview}
          >
            選択した {selectedCount} 件を内容確認済にする
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader />
          </div>
        ) : pending.length === 0 ? (
          <Empty
            title="内容確認待ちはありません"
            description="申込完了 (未確認) の出展者はいなくなりました。"
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    aria-label="すべて選択"
                  />
                </Table.Head>
                <Table.Head>出展者番号</Table.Head>
                <Table.Head>出展者</Table.Head>
                <Table.Head>形態</Table.Head>
                <Table.Head>出展タイトル</Table.Head>
                <Table.Head>出展日</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {pending.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                      aria-label={`#${row.applicationNumber} を選択`}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Link
                      to={`/exhibitors/${row.id}`}
                      target="_blank"
                      rel="noopener"
                      className="inline-flex items-center gap-1 text-kumo-link hover:underline"
                    >
                      #{row.applicationNumber}
                      <ArrowSquareOut size={12} />
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span className="text-sm text-kumo-default">{row.exhibitorName}</span>
                      <span className="text-xs text-kumo-muted">
                        {row.realName} / {row.email}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>{TYPE_LABEL[row.type] ?? row.type}</Table.Cell>
                  <Table.Cell>{row.exhibitTitle}</Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-1">
                      {Array.isArray(row.exhibitDays) && row.exhibitDays.includes('saturday') && (
                        <Badge variant="outline">土</Badge>
                      )}
                      {Array.isArray(row.exhibitDays) && row.exhibitDays.includes('sunday') && (
                        <Badge variant="outline">日</Badge>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>

      <DialogRoot
        role="alertdialog"
        open={previewing !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewing(null);
        }}
      >
        <Dialog className="flex flex-col gap-4 p-6">
          <DialogTitle>内容確認済にしますか?</DialogTitle>
          <DialogDescription>
            {previewing && (
              <>
                選択した <strong>{previewing.eligibleCount}</strong> 件を「内容確認済」にします。
                この操作ではメールは送信されません（内定通知は「メール送信」画面から）。
                {previewing.skippedCount > 0 && (
                  <>
                    <br />
                    条件 (申込完了の当年度出展者) を満たさない {previewing.skippedCount} 件はスキップされます。
                  </>
                )}
              </>
            )}
          </DialogDescription>
          <div className="flex justify-end gap-2">
            <DialogClose
              render={(p) => (
                <Button {...p} variant="secondary">
                  キャンセル
                </Button>
              )}
            />
            <Button onClick={confirmApply} loading={applying} icon={CheckSquare}>
              内容確認済にする
            </Button>
          </div>
        </Dialog>
      </DialogRoot>
    </div>
  );
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string; message?: string } } }).response;
    return r?.data?.error ?? r?.data?.message ?? '操作に失敗しました';
  }
  return e instanceof Error ? e.message : '操作に失敗しました';
}
